import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bar, BarChart, CartesianGrid, Cell, ComposedChart, LabelList, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CalendarDays, Crown, } from 'lucide-react';
import baseapi from '../../baseapi';
import { useTheme } from '../../context/ThemeContext';

/**
 * Festival so far (15 Oct 2026): the Navaratri days side by side, in place of the "city arrivals - today" chart.
 * Day by day: city arrivals by railway station / bus stand / other approaches, with devotees for darshan.
 * Hour by hour: arrivals per hour - today, the busiest day so far and the earlier days' average - to show when the
 * crowd comes. Data: GET /api/festival/trend (festival.py), past days worked out once on the server.
 * Colours checked for colour-blind separation and contrast in both themes.
 */
interface Row {
  day: string; date: string; status: 'past' | 'live' | 'upcoming'; tithi?: string; alankaram?: string;
  arrivals?: { total: number | null; rail: number | null; bus: number | null; other: number | null };
  going_back?: number | null; devotees?: number | null; completed?: number | null;
  peak?: { from: string | null; to: string | null; per_hour: number | null };
  hourly_arrivals?: (number | null)[] | null; hourly_darshan?: (number | null)[] | null; as_of?: number | null; error?: boolean;
}

interface DailyDay {
  day: string;
  date: string;
  status: 'past' | 'live' | 'upcoming';
  alankaram?: string;
  arrivals?: {
    rail: number | null;
    bus: number | null;
    other: number | null;
    total: number | null;
    vs_day_before_pct: number | null;
  };
  darshan?: {
    devotees: number | null;
    completed: number | null;
    vs_day_before_pct: number | null;
  };
}

interface DailySummary {
  arrivals?: {
    all_days_total: number;
    days_counted: number;
    today_so_far: number;
    yesterday_by_now: number;
    vs_yesterday_by_now_times: number;
    usual_busiest_hour?: { hour: string; to: string; people: number; from_days: string[] };
  };
  darshan?: {
    all_days_total: number;
    days_counted: number;
    per_day_average: number;
    most_in_one_day?: { day: string; date: string; alankaram: string; devotees: number };
    today_so_far: number;
    yesterday_by_now: number;
  };
}

interface DailyResponse {
  festival?: string;
  today?: string;
  as_of?: number;
  summary?: DailySummary;
  days?: DailyDay[];
}

const PAL = {
  // 15 Oct 2026: the site's own temple palette - railway #086288 (operators' colour), bus stand saffron, other
  // approaches maroon-red, devotees line in the deep brown of the page text (cream on dark). Checked for colour-blind
  // separation and contrast; dark theme uses lighter / deeper steps of the same hues.
  light: { rail: '#086288', bus: '#E6781B', other: '#A0353A', dev: '#2A1715', avg: '#C2B5A6', grid: '#EFE7DC', axis: '#5F534B' },
  dark: { rail: '#1E87AE', bus: '#DD6A0B', other: '#B03C52', dev: '#F2EADF', avg: '#4B5263', grid: '#262b37', axis: '#a3abbd' },
};
const DAY_COLORS: Record<string, { light: string; dark: string }> = {
  D1: { light: '#3B82F6', dark: '#60A5FA' }, // Blue
  D2: { light: '#0284C7', dark: '#22D3EE' }, // Cyan-blue
  D3: { light: '#8B5CF6', dark: '#A78BFA' }, // Purple
  D4: { light: '#BE123C', dark: '#FB7185' }, // Crimson / Rose
  D5: { light: '#E6781B', dark: '#F08A2C' }, // Saffron / Orange
  D6: { light: '#15803D', dark: '#34D399' }, // Green
  D7: { light: '#A16207', dark: '#FACC15' }, // Gold (Today)
  D8: { light: '#4F46E5', dark: '#818CF8' }, // Indigo
  D9: { light: '#DB2777', dark: '#F472B6' }, // Pink
};
const n = (v: number | null | undefined) => (v == null ? '—' : Math.round(v).toLocaleString('en-IN'));
const dshort = (d: string) => new Date(d + 'T00:00:00Z').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const hh = (h: number) => `${String(h).padStart(2, '0')}:00`;
const dayName = (d: string) => d.replace(/^D(\d+)$/, 'Day $1');   // "D3" -> "Day 3": plain words for everyone
// hour by hour: city arrivals, or devotees through the darshan doors (temple) - 15 Oct 2026
type Metric = 'city' | 'temple';
const METRIC = {
  city: { key: 'hourly_arrivals', what: 'arrivals', title: 'People arriving in the city' },
  temple: { key: 'hourly_darshan', what: 'devotees for darshan', title: 'Devotees for darshan (temple)' },
} as const;

/** tableTarget: an element elsewhere on the page for the comparison table (the Overview puts it in the empty space
 *  under "What needs attention"); without it the table sits under the chart. */
export function FestivalTrend({ tableTarget }: { tableTarget?: HTMLElement | null } = {}) {
  const { theme } = useTheme();
  const c = theme === 'light' ? PAL.light : PAL.dark;
  const [daily, setDaily] = useState<DailyResponse | null>(null);
  const [rows, setRows] = useState<Row[] | null>(null);
  const [view, setView] = useState<'day' | 'hour'>(() => {
    try { return (localStorage.getItem('festival_trend_view_v2') as 'day' | 'hour') || 'hour'; } catch { return 'hour'; }
  });

  const [metric, setMetric] = useState<Metric>(() => {
    try { return (localStorage.getItem('festival_trend_metric_v2') as Metric) || 'temple'; } catch { return 'temple'; }
  });
  useEffect(() => { try { localStorage.setItem('festival_trend_metric_v2', metric); } catch { /* */ } }, [metric]);

  useEffect(() => {
    let alive = true;
    const loadDaily = () => baseapi.get('festival/daily', { timeout: 60000 })
      .then(r => { if (alive && r.data?.days) setDaily(r.data); }).catch(() => { /* keep the last */ });
    const loadTrend = () => baseapi.get('festival/trend', { timeout: 60000 })
      .then(r => { if (alive && r.data?.days) setRows(r.data.days); }).catch(() => { /* keep the last */ });
    loadDaily();
    loadTrend();
    const t = window.setInterval(() => {
      if (!document.hidden) {
        loadDaily();
        loadTrend();
      }
    }, 120000);
    return () => { alive = false; window.clearInterval(t); };
  }, []);
  useEffect(() => { try { localStorage.setItem('festival_trend_view_v2', view); } catch { /* */ } }, [view]);

  const m: Metric = metric;
  const s = useMemo(() => {
    if (!rows && !daily) return null;
    const dailyDays = daily?.days?.filter(r => r.status !== 'upcoming');
    const bars = (dailyDays && dailyDays.length > 0)
      ? dailyDays.map(r => ({
          label: r.day, sub: r.date ? dshort(r.date) : '', alankaram: r.alankaram, live: r.status === 'live',
          rail: r.arrivals?.rail ?? 0, bus: r.arrivals?.bus ?? 0, other: r.arrivals?.other ?? 0,
          total: r.arrivals?.total ?? null, devotees: r.darshan?.devotees ?? null, completed: r.darshan?.completed ?? null,
          vs_arrivals_pct: r.arrivals?.vs_day_before_pct ?? null,
          vs_darshan_pct: r.darshan?.vs_day_before_pct ?? null,
          devPast: r.status === 'live' ? null : r.darshan?.devotees ?? null,
          devToday: r.status === 'live' ? r.darshan?.devotees ?? null : null,
        }))
      : (rows || []).filter(r => r.status !== 'upcoming').map(r => ({
          label: r.day, sub: r.date ? dshort(r.date) : '', alankaram: r.alankaram, live: r.status === 'live',
          rail: r.arrivals?.rail ?? 0, bus: r.arrivals?.bus ?? 0, other: r.arrivals?.other ?? 0,
          total: r.arrivals?.total ?? null, devotees: r.devotees ?? null, completed: r.completed ?? null,
          vs_arrivals_pct: null,
          vs_darshan_pct: null,
          devPast: r.status === 'live' ? null : r.devotees ?? null,
          devToday: r.status === 'live' ? r.devotees ?? null : null,
        }));

    const done = (rows || []).filter(r => r.status !== 'upcoming' && !r.error && r.arrivals);
    const past = done.filter(r => r.status === 'past');
    const today = done.find(r => r.status === 'live');
    const now = new Date(Date.now() + 19800e3);
    const hr = now.getUTCHours(), frac = now.getUTCMinutes() / 60;
    const byNow = (r: Row) => (r.hourly_arrivals || []).slice(0, hr).reduce((a: number, v) => a + (v || 0), 0) + ((r.hourly_arrivals || [])[hr] || 0) * frac;
    const yesterday = past[past.length - 1];
    const festivalTotal = daily?.summary?.darshan?.all_days_total ?? done.reduce((a, r) => a + (r.devotees || 0), 0);
    const busiest = daily?.summary?.darshan?.most_in_one_day
      ? { day: daily.summary.darshan.most_in_one_day.day, date: daily.summary.darshan.most_in_one_day.date, alankaram: daily.summary.darshan.most_in_one_day.alankaram, devotees: daily.summary.darshan.most_in_one_day.devotees }
      : past.reduce<Row | null>((b, r) => ((r.devotees || 0) > (b?.devotees || 0) ? r : b), null);
    // the hour that brings the most people on an average earlier day (city arrivals or temple, as chosen)
    const hk = METRIC[m].key;
    const hpast = past.filter(r => r[hk]);
    const avg = Array.from({ length: 24 }, (_, h) => hpast.length ? hpast.reduce((a, r) => a + ((r[hk] || [])[h] || 0), 0) / hpast.length : 0);
    const peakH = avg.reduce((b, v, h) => (v > avg[b] ? h : b), 0);
    const allWithData = done.filter(r => r[hk] && Array.isArray(r[hk]));
    const activeDays = allWithData.map(r => ({
      day: r.day,
      date: r.date,
      alankaram: r.alankaram,
      isLive: r.status === 'live',
    }));
    const hours = Array.from({ length: 24 }, (_, h) => {
      const row: Record<string, number | string | null> = { hour: hh(h), avg: hpast.length ? Math.round(avg[h]) : null };
      allWithData.forEach(r => {
        if (r.status === 'live') {
          const val = r[hk]?.[h];
          row[r.day] = val != null && (h < hr || (h === hr && val > 0)) ? val : null;
          row.today = row[r.day];
        } else {
          row[r.day] = r[hk]?.[h] ?? null;
        }
      });
      let hourSum = 0;
      let hasData = false;
      allWithData.forEach(r => {
        const val = row[r.day];
        if (typeof val === 'number' && val > 0) {
          hourSum += val;
          hasData = true;
        }
      });
      row.total = hasData && hourSum > 0 ? hourSum : null;
      return row;
    });
    const todayNow = daily?.summary?.arrivals?.today_so_far ?? (today ? (today.arrivals?.total ?? byNow(today)) : null);
    const yNow = daily?.summary?.arrivals?.yesterday_by_now ?? (yesterday ? byNow(yesterday) : null);
    // today's busiest completed hour, against a usual festival day at that hour
    let todayPeak: { h: number; v: number; usual: number } | null = null;
    for (let h = 0; h < hr; h++) {
      const v = today?.[hk]?.[h] ?? null;
      if (v != null && (!todayPeak || v > todayPeak.v)) todayPeak = { h, v, usual: avg[h] };
    }
    const pastNames = hpast.length ? (hpast.length > 2 ? `${dayName(hpast[0].day)} to ${dayName(hpast[hpast.length - 1].day)}` : hpast.map(p => dayName(p.day)).join(', ')) : '';
    // each day's busiest hour, for the table under the hour view (complete hours only for today)
    const dayPeaks = [...hpast, ...(today && today[hk] ? [today] : [])].map(r => {
      const arr = (r[hk] || []).map((v, h) => (r.status === 'live' && h >= hr ? null : v));
      let ph = -1;
      arr.forEach((v, h) => { if (v != null && (ph < 0 || v > (arr[ph] ?? 0))) ph = h; });
      return {
        day: r.day, date: r.date, alankaram: r.alankaram, live: r.status === 'live', ph,
        // whole day: the day's own total (the same figure as the day table), not the sum of the hours
        peak: ph >= 0 ? arr[ph] ?? null : null, total: (m === 'temple' ? r.devotees : r.arrivals?.total) ?? null
      };
    });
    const daysCount = daily?.summary?.arrivals?.days_counted ?? done.length;
    return { bars, dayPeaks, past, hpast, today, yesterday, festivalTotal, busiest, peakH, avgPeak: avg[peakH], hours, hr, todayNow, yNow, days: daysCount, todayPeak, pastNames, activeDays };
  }, [rows, daily, m]);

  const axis = { fontSize: 13, fontWeight: 600, fill: c.axis };
  const seg = (on: boolean) => `px-3 py-1 text-sm font-bold rounded-md cursor-pointer transition ${on
    ? 'bg-white dark:bg-[#262b37] text-[#761D20] dark:text-orange-400 shadow-xs' : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200'}`;
  // the same figures as a table, the festival days side by side (15 Oct 2026: operators asked for a comparison they can
  // read off without pointing at the chart); on the Overview it sits in the card next to the chart. Day-wise only for
  // now (an hour-by-hour table was asked for, then held back)
  const th = 'text-right [&>th]:px-2 [&>th]:py-2.5 [&>th]:font-bold [&>th]:leading-tight [&>th]:align-middle';
  const td = 'text-right border-t border-neutral-100 dark:border-[#262b37] [&>td]:px-2 [&>td]:py-2.5 [&>td]:align-middle';
  const muted = 'text-neutral-600 dark:text-neutral-400';
  const todayTag = (
    <div className="mt-0.5">
      <span className="px-1.5 py-0.5 rounded bg-[#761D20] text-white text-[10.5px] font-bold leading-tight inline-block">
        today
      </span>
    </div>
  );
  const title = (t: string) => (
    <div className="mb-1.5 flex items-center justify-between gap-2 shrink-0">
      <div className="text-sm font-black text-neutral-900 dark:text-neutral-100 flex items-center gap-1.5">
        <CalendarDays className="w-4 h-4 text-[#E06A10] dark:text-[#F08A2C] stroke-[2.5]" />{t}
      </div>
    </div>
  );
  const table = s ? (
    <div className="flex flex-col">
      {tableTarget && title('Navaratri Daily Footfall Comparison')}
      <div className={`${tableTarget ? '' : 'mt-2 '}flex flex-col overflow-x-auto rounded-lg border border-neutral-200/80 dark:border-[#262b37]`}>
        <table className="w-full text-[13px] tabular-nums border-collapse">
          <thead className={`bg-neutral-50 dark:bg-[#141821] ${muted} shrink-0`}>
            <tr className={th}>
              <th className="text-left">Day</th>
              <th><Swatch col={c.rail} />Railway</th>
              <th><Swatch col={c.bus} />Bus stand</th>
              <th><Swatch col={c.other} />Other roads</th>
              <th>City total</th>
              {/* the header ends over the number, not over the crown space after it */}
              <th className="!pr-6"><Swatch col={c.dev} round />Devotees for darshan</th>
              <th>vs day before</th>
            </tr>
          </thead>
          <tbody className="text-neutral-900 dark:text-neutral-100 h-full">
            {s.bars.map((b, i) => {
              const prev = i > 0 ? s.bars[i - 1] : null;
              const d = metric === 'temple'
                ? (b.vs_darshan_pct ?? (!b.live && prev?.devotees && b.devotees != null ? Math.round(100 * (b.devotees / prev.devotees - 1)) : null))
                : (b.vs_arrivals_pct ?? (!b.live && prev?.total && b.total != null ? Math.round(100 * (b.total / prev.total - 1)) : null));
              const top = !b.live && s.busiest && b.label === s.busiest.day;
              return (
                <tr key={b.label} className={`${td} ${b.live ? 'bg-[#FBF3EA] dark:bg-white/[0.03]' : ''}`}>
                  <td className="text-left" title={b.alankaram || undefined}>
                    <div className="whitespace-nowrap flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-xs shrink-0" style={{ background: theme === 'light' ? DAY_COLORS[b.label]?.light || '#3B82F6' : DAY_COLORS[b.label]?.dark || '#60A5FA' }} />
                      <span><b>{dayName(b.label)}</b> <span className={muted}>· {b.sub}</span></span>
                    </div>
                    {b.live && todayTag}
                    {b.alankaram && !tableTarget && <div className={`text-xs leading-tight ${muted}`}>{b.alankaram}</div>}
                  </td>
                  <td>{n(b.rail)}</td>
                  <td>{n(b.bus)}</td>
                  <td>{n(b.other)}</td>
                  <td className="font-bold">{n(b.total)}</td>
                  {/* 15 Oct 2026: a fixed space for the crown on every row, so the numbers line up */}
                  <td className="font-black whitespace-nowrap">{n(b.devotees)}<span className="inline-block w-4 ml-1 text-left align-[-2px]">
                    {top && <Crown className="w-3.5 h-3.5 text-[#C99A1E]" aria-label="most in one day" />}</span></td>
                  <td className="whitespace-nowrap font-bold">
                    {b.live ? <span className={`${muted} font-semibold`}>so far</span>
                      : d == null ? <span className={muted}>—</span>
                        : <span className={d >= 0 ? 'text-[#C2620F] dark:text-[#F08A2C]' : 'text-[#336A91] dark:text-[#A3C8E2]'}>{d >= 0 ? `▲ ${d}% more` : `▼ ${-d}% fewer`}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  ) : null;

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm sm:text-base font-black text-neutral-900 dark:text-neutral-100 flex items-center gap-1.5">
            <CalendarDays className="w-4 h-4 text-[#E06A10] dark:text-[#F08A2C] stroke-[2.5]" />
            <span>Navaratri {view === 'hour' ? 'Hourly' : 'Daily'} Devotee Count</span>
          </h3>
          <div className="text-sm font-semibold text-neutral-600 dark:text-neutral-400 mt-0.5">
            {view === 'day'
              ? (metric === 'city'
                  ? 'City arrivals at the railway station, bus stand, and other approaches, every festival day'
                  : 'Devotees for darshan through the sanctum doors, every festival day')
              : (metric === 'city'
                  ? 'When people arrive in the city across each festival day, hour by hour'
                  : 'Devotees through the darshan doors across each festival day, hour by hour')}
          </div>
        </div>
        <div className="flex rounded-lg bg-neutral-100 dark:bg-[#141821] p-0.5 border border-neutral-200/80 dark:border-[#262b37]">
          <button onClick={() => setView('day')} className={seg(view === 'day')}>Daily </button>
          <button onClick={() => setView('hour')} className={seg(view === 'hour')}>Hourly</button>
        </div>
      </div>

      {/* key figures */}
      {/* <div className="mt-2 grid grid-cols-2 xl:grid-cols-4 gap-1.5">
        {[
          { icon: Users, label: 'Devotees – all days', value: s ? n(s.festivalTotal) : null, sub: s ? `${s.days} days · about ${n(s.festivalTotal / Math.max(1, s.days))} a day` : '' },
          {
            icon: Crown, label: 'Most in one day', value: s?.busiest ? n(s.busiest.devotees) : '—',
            sub: s?.busiest ? `${dayName(s.busiest.day)} · ${dshort(s.busiest.date)}${s.busiest.alankaram ? ` · ${s.busiest.alankaram}` : ''}` : ''
          },
          {
            icon: TrendingUp, label: 'Reached the city today',
            value: s?.todayNow != null ? n(s.todayNow) : '—', sub: vsYesterday
          },
          {
            icon: Clock,
            label: view === 'hour'
              ? (m === 'temple' ? "Today's peak darshan hour" : "Today's peak arrival hour")
              : (m === 'temple' ? 'Darshan rush, most days' : 'City rush, most days'),
            value: view === 'hour'
              ? (s?.todayPeak ? ampm(s.todayPeak.h) : '—')
              : (s && s.hpast.length ? ampm(s.peakH) : '—'),
            sub: view === 'hour'
              ? (s?.todayPeak ? `${n(s.todayPeak.v)} ${m === 'temple' ? 'devotees' : 'people'}` : 'Calculating…')
              : (s && s.hpast.length ? `about ${n(s.avgPeak)} ${m === 'temple' ? 'devotees' : 'people'} in that hour` : '')
          },
        ].map(t => (
          <div key={t.label} className="px-2.5 py-2 rounded-lg bg-neutral-50 dark:bg-[#141821] border border-neutral-200/80 dark:border-[#262b37] min-w-0">
            <div className="flex items-start gap-1.5 text-[13px] font-bold text-neutral-700 dark:text-neutral-300 leading-snug">
              <t.icon className="w-4 h-4 mt-px shrink-0" /><span>{t.label}</span>
            </div>
            <div className="mt-0.5 text-2xl font-black text-neutral-900 dark:text-neutral-100 leading-tight truncate">
              {t.value ?? <span className="inline-block w-16 h-5 rounded bg-neutral-200 dark:bg-[#262b37] animate-pulse align-middle" />}
            </div>
            <div className="mt-0.5 text-[13px] font-semibold text-neutral-600 dark:text-neutral-400 leading-snug">{t.sub}</div>
          </div>
        ))}
      </div> */}

      <div className="mt-2.5 flex items-center gap-2 text-sm font-bold text-neutral-700 dark:text-neutral-300">
        <span>Show:</span>
        <div className="flex rounded-lg bg-neutral-100 dark:bg-[#141821] p-0.5 border border-neutral-200/80 dark:border-[#262b37]">
          <button onClick={() => setMetric('city')} className={seg(metric === 'city')}>City arrivals</button>
          <button onClick={() => setMetric('temple')} className={seg(metric === 'temple')}>Temple darshan</button>
        </div>
      </div>

      {/* chart */}
      <div className="mt-1 h-[270px] w-full">
        {!s ? (
          <div className="h-full flex items-center justify-center text-sm text-neutral-600 dark:text-neutral-400">Loading the festival days…</div>
        ) : view === 'day' ? (
          metric === 'city' ? (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={s.bars} margin={{ top: 20, right: 8, left: 0, bottom: 0 }} barCategoryGap="22%">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={c.grid} />
                <XAxis dataKey="label" tick={axis} tickLine={false} axisLine={{ stroke: c.grid }}
                  tickFormatter={(v: string) => { const b = s.bars.find(x => x.label === v); return b ? `${dayName(v)} · ${b.sub}${b.live ? ' (today)' : ''}` : v; }} />
                <YAxis tick={axis} tickLine={false} axisLine={false} width={72} tickFormatter={(v: number) => n(v)} />
                <Tooltip cursor={{ fill: theme === 'light' ? 'rgba(118,29,32,0.05)' : 'rgba(255,255,255,0.04)' }}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const b = payload[0].payload;
                    return (
                      <div className="bg-white dark:bg-[#161822] p-2.5 rounded-xl border border-neutral-200 dark:border-neutral-700 shadow-xl text-[13px] space-y-1 min-w-[240px]">
                        <div className="font-black text-neutral-900 dark:text-neutral-100 flex items-center justify-between gap-2 border-b border-neutral-100 dark:border-neutral-800 pb-1">
                          <span>{dayName(b.label)} · {b.sub}{b.live ? ' · today so far' : ''}</span>
                          {b.vs_arrivals_pct != null && (
                            <span className={`text-xs font-bold ${b.vs_arrivals_pct >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                              {b.vs_arrivals_pct >= 0 ? `▲ +${b.vs_arrivals_pct}%` : `▼ ${b.vs_arrivals_pct}%`}
                            </span>
                          )}
                        </div>
                        {b.alankaram && <div className="text-neutral-600 dark:text-neutral-400">{b.alankaram}</div>}
                        {[['Railway station', b.rail, c.rail], ['Bus stand', b.bus, c.bus], ['Other approaches', b.other, c.other]].map(([l, v, col]) => (
                          <div key={l as string} className="flex justify-between gap-3"><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm" style={{ background: col as string }} />{l}</span><b className="tabular-nums">{n(v as number)}</b></div>
                        ))}
                        <div className="flex justify-between gap-3 border-t border-neutral-100 dark:border-neutral-800 pt-1"><span>City arrivals</span><b className="tabular-nums">{n(b.total)}</b></div>
                        <div className="flex justify-between gap-3"><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: c.dev }} />Devotees for darshan</span><b className="tabular-nums">{n(b.devotees)}</b></div>
                      </div>
                    );
                  }} />
                <Bar dataKey="rail" name="Railway station" stackId="a" fill={c.rail} isAnimationActive={false} />
                <Bar dataKey="bus" name="Bus stand" stackId="a" fill={c.bus} isAnimationActive={false} />
                <Bar dataKey="other" name="Other approaches" stackId="a" fill={c.other} radius={[4, 4, 0, 0]} isAnimationActive={false}>
                  <LabelList
                    dataKey="total"
                    position="top"
                    formatter={(v: any) => typeof v === 'number' && v > 0 ? n(v) : ''}
                    fill={theme === 'light' ? '#1e293b' : '#f1f5f9'}
                    fontSize={11}
                    fontWeight={800}
                    offset={5}
                  />
                </Bar>
                <Line dataKey="devPast" name="Devotees for darshan" stroke={c.dev} strokeWidth={2.5} dot={{ r: 4, fill: c.dev, stroke: theme === 'light' ? '#fff' : '#181b23', strokeWidth: 2 }} isAnimationActive={false} />
                <Line dataKey="devToday" name="Devotees today so far" stroke="none" dot={{ r: 5, fill: theme === 'light' ? '#fff' : '#181b23', stroke: c.dev, strokeWidth: 2.5 }} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={s.bars} margin={{ top: 20, right: 8, left: 0, bottom: 0 }} barCategoryGap="22%">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={c.grid} />
                <XAxis dataKey="label" tick={axis} tickLine={false} axisLine={{ stroke: c.grid }}
                  tickFormatter={(v: string) => { const b = s.bars.find(x => x.label === v); return b ? `${dayName(v)} · ${b.sub}${b.live ? ' (today)' : ''}` : v; }} />
                <YAxis tick={axis} tickLine={false} axisLine={false} width={72} tickFormatter={(v: number) => n(v)} />
                <Tooltip cursor={{ fill: theme === 'light' ? 'rgba(118,29,32,0.05)' : 'rgba(255,255,255,0.04)' }}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const b = payload[0].payload;
                    const col = theme === 'light' ? DAY_COLORS[b.label]?.light || '#3B82F6' : DAY_COLORS[b.label]?.dark || '#60A5FA';
                    return (
                      <div className="bg-white dark:bg-[#161822] p-2.5 rounded-xl border border-neutral-200 dark:border-neutral-700 shadow-xl text-[13px] space-y-1.5 min-w-[240px]">
                        <div className="font-black text-neutral-900 dark:text-neutral-100 flex items-center justify-between gap-2 border-b border-neutral-100 dark:border-neutral-800 pb-1">
                          <span className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-xs shrink-0" style={{ background: col }} />
                            <span>{dayName(b.label)} · {b.sub}{b.live ? ' · today so far' : ''}</span>
                          </span>
                          {b.vs_darshan_pct != null && (
                            <span className={`text-xs font-bold ${b.vs_darshan_pct >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                              {b.vs_darshan_pct >= 0 ? `▲ +${b.vs_darshan_pct}%` : `▼ ${b.vs_darshan_pct}%`}
                            </span>
                          )}
                        </div>
                        {b.alankaram && <div className="text-xs font-semibold text-neutral-600 dark:text-neutral-400">{b.alankaram}</div>}
                        <div className="flex justify-between gap-3 text-sm font-black pt-0.5">
                          <span className="text-neutral-700 dark:text-neutral-300">Devotees for darshan:</span>
                          <span className="tabular-nums" style={{ color: col }}>{n(b.devotees)}</span>
                        </div>
                        {b.completed != null && (
                          <div className="flex justify-between gap-3 text-xs text-neutral-600 dark:text-neutral-400">
                            <span>Completed darshan:</span>
                            <span className="tabular-nums font-bold text-neutral-800 dark:text-neutral-200">{n(b.completed)}</span>
                          </div>
                        )}
                        {b.total != null && (
                          <div className="flex justify-between gap-3 text-xs border-t border-neutral-100 dark:border-neutral-800 pt-1 text-neutral-500 dark:text-neutral-400">
                            <span>City arrivals:</span>
                            <span className="tabular-nums font-bold text-neutral-700 dark:text-neutral-300">{n(b.total)}</span>
                          </div>
                        )}
                      </div>
                    );
                  }} />
                <Bar dataKey="devotees" name="Devotees for darshan" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                  {s.bars.map((entry) => {
                    const col = theme === 'light' ? DAY_COLORS[entry.label]?.light || '#3B82F6' : DAY_COLORS[entry.label]?.dark || '#60A5FA';
                    return <Cell key={`cell-${entry.label}`} fill={col} />;
                  })}
                  <LabelList
                    dataKey="devotees"
                    position="top"
                    formatter={(v: any) => typeof v === 'number' && v > 0 ? n(v) : ''}
                    fill={theme === 'light' ? '#1e293b' : '#f1f5f9'}
                    fontSize={11}
                    fontWeight={800}
                    offset={5}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={s.hours} margin={{ top: 22, right: 8, left: 0, bottom: 0 }} barCategoryGap="16%" barGap={1}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={c.grid} />
              <XAxis dataKey="hour" tick={axis} tickLine={false} axisLine={{ stroke: c.grid }} interval={1} />
              <YAxis tick={axis} tickLine={false} axisLine={false} width={56} tickFormatter={(v: number) => n(v)} />
              <Tooltip cursor={{ fill: theme === 'light' ? 'rgba(118,29,32,0.05)' : 'rgba(255,255,255,0.04)' }}
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const r = payload[0].payload;
                  const h = Number(String(label).slice(0, 2));
                  return (
                    <div className="bg-white dark:bg-[#161822] p-2.5 rounded-xl border border-neutral-200 dark:border-neutral-700 shadow-xl text-[13px] space-y-1 min-w-[220px]">
                      <div className="font-black text-neutral-900 dark:text-neutral-100 pb-1 border-b border-neutral-100 dark:border-neutral-800">
                        {METRIC[m].title} · {label}–{hh((h + 1) % 24)}
                      </div>
                      <div className="space-y-1 pt-0.5">
                        {(s.activeDays || []).map(d => {
                          const val = r[d.day];
                          const col = theme === 'light' ? DAY_COLORS[d.day]?.light || '#3B82F6' : DAY_COLORS[d.day]?.dark || '#60A5FA';
                          return (
                            <div key={d.day} className="flex items-center justify-between gap-3 text-xs sm:text-[13px]">
                              <span className="flex items-center gap-1.5 font-bold" style={{ color: col }}>
                                <span className="w-2.5 h-2.5 rounded-xs shrink-0" style={{ background: col }} />
                                <span>{dayName(d.day)}{d.isLive ? ' (Today)' : ''}</span>
                              </span>
                              <span className="tabular-nums font-black text-neutral-900 dark:text-neutral-100">
                                {val != null ? n(val) : d.isLive && h >= s.hr ? <span className="text-neutral-400 font-normal">not yet</span> : '—'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      {(() => {
                        const validVals = (s.activeDays || [])
                          .map(d => r[d.day])
                          .filter((v): v is number => typeof v === 'number');
                        if (!validVals.length) return null;
                        const total = validVals.reduce((sum, v) => sum + v, 0);
                        return (
                          <div className="flex items-center justify-between gap-3 text-xs sm:text-[13px] pt-1.5 mt-1 border-t border-neutral-200 dark:border-neutral-700/80 font-black">
                            <span className="text-neutral-800 dark:text-neutral-200">
                              Overall Total
                            </span>
                            <span className="tabular-nums text-neutral-900 dark:text-neutral-100">
                              {n(total)}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  );
                }} />
              {(s.activeDays || []).map(d => (
                <Bar
                  key={d.day}
                  dataKey={d.day}
                  name={d.isLive ? `${dayName(d.day)} (Today)` : dayName(d.day)}
                  fill={theme === 'light' ? DAY_COLORS[d.day]?.light || '#3B82F6' : DAY_COLORS[d.day]?.dark || '#60A5FA'}
                  radius={[2, 2, 0, 0]}
                  isAnimationActive={false}
                >
                  <LabelList
                    content={(props: any) => {
                      const { x, y, width, index } = props;
                      if (x == null || y == null || index == null) return null;
                      const row = s?.hours?.[index];
                      if (!row) return null;
                      // Find tallest day for this hour
                      let maxDay: string | null = null;
                      let maxVal = 0;
                      (s.activeDays || []).forEach(dayObj => {
                        const v = Number(row[dayObj.day]) || 0;
                        if (v > maxVal) {
                          maxVal = v;
                          maxDay = dayObj.day;
                        }
                      });
                      if (maxDay !== d.day) return null;
                      const total = row.total;
                      if (!total || typeof total !== 'number' || total <= 0) return null;
                      const text = total >= 100000 ? `${Math.round(total / 1000)}k` : n(total);
                      const dIndex = (s.activeDays || []).findIndex(x => x.day === d.day);
                      const totalDays = (s.activeDays || []).length;
                      const groupStartX = x - dIndex * (width + 1);
                      const groupCenterX = groupStartX + (totalDays * (width + 1) - 1) / 2;
                      return (
                        <text
                          x={groupCenterX}
                          y={y - 5}
                          textAnchor="middle"
                          fill={theme === 'light' ? '#1e293b' : '#f1f5f9'}
                          fontSize={9.5}
                          fontWeight={800}
                          className="select-none pointer-events-none"
                        >
                          {text}
                        </text>
                      );
                    }}
                  />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* legend, in the order of the bars */}
      <div className="mt-2.5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[13px] font-bold text-neutral-700 dark:text-neutral-300">
        {(view === 'day' && metric === 'city'
          ? [['Railway station', c.rail, 'box'], ['Bus stand', c.bus, 'box'], ['Other approaches', c.other, 'box'], ['Devotees for darshan', c.dev, 'line'], ['Today so far', c.dev, 'ring']]
          : (s?.bars || []).map(b => [
              b.live ? `${dayName(b.label)} (Today)` : dayName(b.label),
              theme === 'light' ? DAY_COLORS[b.label]?.light || '#3B82F6' : DAY_COLORS[b.label]?.dark || '#60A5FA',
              'box',
            ])
        ).map(([l, col, kind]) => (
          <span key={l} className="inline-flex items-center gap-1.5">
            {kind === 'box' ? <span className="w-3 h-3 rounded-sm" style={{ background: col }} />
              : kind === 'ring' ? <span className="w-3 h-3 rounded-full border-2" style={{ borderColor: col }} />
                : <span className="w-4 h-0" style={{ borderTop: `${kind === 'dash' ? '2px dashed' : '3px solid'} ${col}` }} />}
            {l}
          </span>
        ))}
      </div>

      {table && !tableTarget && table}
      {table && tableTarget && createPortal(table, tableTarget)}
    </div>
  );
}

const Swatch = ({ col, round }: { col: string; round?: boolean }) => (
  <span className={`inline-block w-2.5 h-2.5 mr-1 align-[-1px] ${round ? 'rounded-full' : 'rounded-sm'}`} style={{ background: col }} />
);
